import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;
const REFRESH_URL = "refresh";

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Utility to sanitize values
const sanitize = (value) => (typeof value === "string" ? value.trim() : value);

// Sanitize recursively if needed
const sanitizeObject = (obj) =>
  obj && typeof obj === "object"
    ? Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, sanitize(v)]))
    : obj;

// Add interceptor
api.interceptors.request.use((config) => {
  if (config.params) {
    config.params = sanitizeObject(config.params);
  }
  if (config.data) {
    config.data = sanitizeObject(config.data);
  }
  return config;
});

export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
};

export const refreshAccessToken = () => api.post(REFRESH_URL);
export const logoutUser = () => api.post("logout");

let updateAccessToken = null;
export const injectUpdateAccessToken = (updater) => {
  updateAccessToken = updater;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest.url !== REFRESH_URL &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const response = await refreshAccessToken();
        const { access_token } = response.data;

        if (updateAccessToken) {
          updateAccessToken(access_token);
        }
        setAuthToken(access_token);

        originalRequest.headers["Authorization"] = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        if (updateAccessToken) {
          updateAccessToken(null);
        }
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// --- API Function Definitions

// Jobs related
export const postStartTransform = (
  srcUuid,
  etlCfgUuid,
  extractionId,
  extractionLabel
) => {
  api.post("jobs/transformation", {
    src_uuid: srcUuid,
    etl_cfg_uuid: etlCfgUuid,
    extraction_id: extractionId,
    extraction_label: extractionLabel,
  });
};
export const getJobExecutions = (page = 1, itemsPerPage = 10) =>
  api.get("jobs/job-execs", {
    params: {
      page,
      items_per_page: itemsPerPage,
    },
    paramsSerializer: {
      indexes: null,
    },
  });
export const getJobStatus = (id) =>
  api.get("jobs/job-status", {
    params: {
      uuid: id,
    },
    paramsSerializer: {
      indexes: null,
    },
  });
export const postStartProfilerJobs = () => api.post("jobs/profiler");

// Source related
export const getSourcesSingleExternal = (page = 1, itemsPerPage = 10) =>
  api.get(`srcs`, {
    params: {
      page,
      items_per_page: itemsPerPage,
      composite_types: "SingleExternal",
    },
  });
export const getSourcesSingleInternal = ({ page = 1, itemsPerPage = 10 }) =>
  api.get("srcs", {
    params: {
      page,
      items_per_page: itemsPerPage,
      composite_types: "SingleInternal",
    },
  });
export const getSourceEtlConfig = (page = 1, itemsPerPage = 10, id) =>
  api.get(`src/${id}/etl_cfgs`, {
    params: {
      page,
      items_per_page: itemsPerPage,
    },
  });
export const getSourceById = (id) => api.get(`src/${id}`);

// User related
export const getUsers = (page = 1, itemsPerPage = 10) =>
  api.get(`users`, { params: { page, items_per_page: itemsPerPage } });
export const logoutUserByAdmin = (username) =>
  api.post("logout-user", { username });
export const addUser = (userData) => api.post("user", userData);
export const deleteUser = (username) => api.delete(`user/${username}`);
export const getUserMe = () => api.get("user/me");

// Extraction related
export const getExtractions = (page = 1, itemsPerPage = 10) =>
  api.get(`extractions`, { params: { page, items_per_page: itemsPerPage } });
export const getExtractionStats = (srcUuid = null, etlCfgUuid = null) =>
  api.get("extractions/get_storage_stats", {
    params: {
      src_uuid: srcUuid || undefined,
      etl_cfg_uuid: etlCfgUuid || undefined,
    },
  });
export const addLabelToExtraction = (extractionId, etlConfigUuid, label) =>
  api.post("extractions/add_label", {
    extraction_id: extractionId,
    etl_cfg_uuid: etlConfigUuid,
    label,
  });
export const downloadExtractionFiles = ({
  extraction_id,
  src_uuid,
  etl_cfg_uuid,
  label,
  file_name_filter,
}) =>
  api.get("extractions/download_extraction_files", {
    params: {
      extraction_id,
      src_uuid,
      etl_cfg_uuid,
      label,
      file_name_filter,
    },
    responseType: "blob",
  });
export const startExtractionJob = (sourceId, etlCfgUuid) =>
  api.post("jobs/extraction", {
    src_uuid: sourceId,
    etl_cfg_uuid: etlCfgUuid,
  });

// Aggregation realted
export const startAggregationJob = (sourceId) =>
  api.post("jobs/aggregation", {
    src_uuid: sourceId,
  });

// Node analysis related
export const getNodes = ({ page = 1, itemsPerPage = 10, node_type, name } = {}) =>
  api.get("applications/quontx_network/nodes", {
    params: {
      page,
      items_per_page: itemsPerPage,
      node_type,
      name: name,
    },
    paramsSerializer: {
      indexes: null,
    },
  });


export const getNodeById = (id) => api.get(`applications/quontx_network/node/${id}`);

export const getNetworkNodeSets = ({ page = 1, itemsPerPage = 10, name } = {}) =>
  api.get("applications/quontx_network/node_sets", {
    params: {
      page,
      items_per_page: itemsPerPage,
      name: name,
    },
    paramsSerializer: {
      indexes: null,
    },
  });

export const getRiskNodeSets = ({ page = 1, itemsPerPage = 10, name } = {}) =>
  api.get("applications/quontx_risk/node_sets", {
    params: {
      page,
      items_per_page: itemsPerPage,
      name: name,
    },
    paramsSerializer: {
      indexes: null,
    },
  });

export const getProfilers = ({ page = 1, itemsPerPage = 10, entities } = {}) =>
  api.get("applications/quontx_network/node_analysis/profiler", {
    params: {
      page,
      items_per_page: itemsPerPage,
      entity_uuids: entities,
    },
    paramsSerializer: {
      indexes: null,
    },
  });
export const getProfiles = ({ page = 1, itemsPerPage = 10, entities } = {}) =>
  api.get("applications/quontx_network/node_analysis/profiler", {
    params: {
      page,
      items_per_page: itemsPerPage,
      entity_uuids: entities,
    },
    paramsSerializer: {
      indexes: null,
    },
  });
export const getProfilerMetadata = ({
  page = 1,
  itemsPerPage = 10,
  entities,
} = {}) =>
  api.get("applications/quontx_network/node_analysis/profiler", {
    params: {
      meta_only: true,
      page,
      items_per_page: itemsPerPage,
      entity_uuids: entities,
    },
    paramsSerializer: {
      indexes: null,
    },
  });
export const getProfilerComparator = ({ nodeA, nodeB }) =>
  api.get("applications/quontx_network/node_analysis/profile_comparator", {
    responseType: 'arraybuffer',
    params: { node_a: nodeA, node_b: nodeB },
    paramsSerializer: {
      indexes: null,
    },
  });

export const getNodeSetNodes = ({
  page = 1,
  itemsPerPage = 10,
  nodeSetId,
} = {}) =>
  api.get(`applications/quontx_network/node_set/${nodeSetId}/nodes`, {
    params: {
      page,
      items_per_page: itemsPerPage,
    },
    paramsSerializer: {
      indexes: null,
    },
  });

// Edge analysis realted
export const getEdgeQuantifier = ({
  page = 1,
  itemsPerPage = 10,
  nodes,
  edgeType,
} = {}) =>
  api.get("applications/quontx_network/edge_analysis/edge_quantifier", {
    params: {
      page,
      items_per_page: itemsPerPage,
      nodes: nodes,
      type: edgeType,
    },
    paramsSerializer: {
      indexes: null,
    },
  });

// Risk Related
export const getRiskMutual = ({
  entity_uuids = [],
  stats_type
} = {}) =>
  api.get("applications/quontx_risk/mutual", {
    responseType: 'arraybuffer',
    params: { entity_uuids, stats_type },
    paramsSerializer: {
      indexes: null,
      stats_type: stats_type,
    },
  });

export const getRiskMutualGraph = ({
  entity_uuids = [],
  stats_type
} = {}) =>
  api.get("applications/quontx_risk/mutual", {
    responseType: 'json',
    params: { entity_uuids, stats_type },
    paramsSerializer: {
      indexes: null,
      stats_type: stats_type,
    },
  });

export const getRiskProfile = ({
  entity_uuid,
  stats_type,
} = {}) =>
  api.get(`applications/quontx_risk/profile/2o/${entity_uuid}`, {
    responseType: 'arraybuffer',
    params: {stats_type},
    paramsSerializer: {
      indexes: null,
    },
  });

export const getRiskSimilarity = ({
  node_a,
  node_b
} = {}) =>
  api.get("applications/quontx_risk/similarity", {
    responseType: 'arraybuffer',
    params: { node_a, node_b },
    paramsSerializer: {
      indexes: null,
    },
  });


// Delete cache Related
export const deleteCache = () =>
  api.delete("applications/quontx_network/profiles_cache");

export const getTopRelated = ({ k = 10, node, schema } = {}) =>
  api.get("applications/quontx_network/graph_analysis/top_k_neighbors", {
    params: {
      schema: schema,
      node: node,
      k: k,
    },
    paramsSerializer: {
      indexes: null,
    },
  });
